<script setup lang="ts">
import { AwIcon, AwSkeleton } from "@asap/design";
import { statusColor, UNAVAILABLE_KPI_LABEL, MISSING_KPI_VALUE, type ChartStatus } from "./theme";
import type { HighlightItem } from "./types";

const SKELETON_COUNT = 6;

withDefaults(
  defineProps<{
    title?: string;
    highlights?: HighlightItem[];
    loading?: boolean;
  }>(),
  { title: "À retenir", highlights: undefined, loading: false }
);

const statusIconName = (status: ChartStatus) => {
  if (status === "success") return "i-ph-check-circle-fill";
  if (status === "critical") return "i-ph-x-circle-fill";
  if (status === "warning") return "i-ph-warning-circle-fill";

  return "i-ph-minus-circle-fill";
};

const ui = {
  grid: "grid grid-cols-2 gap-1.5",
  item: "flex items-center justify-between gap-2 rounded-lg border border-secondary-200 bg-white px-2 py-1.5",
  unavailableItem: "border-dashed bg-secondary-50",
  label: "text-sm text-secondary-900",
  unavailableLabel: "text-sm text-secondary-700",
  unavailableMention: "shrink-0 text-right text-xs leading-tight text-secondary-700",
  missingValue: "shrink-0 text-sm font-medium text-secondary-700",
  statusIcon: "size-4 shrink-0",
  skeletonLabel: "h-3.5 w-20",
  skeletonIcon: "size-4 shrink-0 rounded-full",
};

const statusIconStyle = (status: ChartStatus) => ({
  color: statusColor(status),
});
</script>

<template>
  <ConsultantKpiCard :title="title" flat>
    <div :class="ui.grid">
      <template v-if="loading">
        <div v-for="index in SKELETON_COUNT" :key="`highlight-skeleton-${index}`" :class="ui.item">
          <AwSkeleton :class="ui.skeletonLabel" />
          <AwSkeleton :class="ui.skeletonIcon" />
        </div>
      </template>
      <template v-else>
        <div
          v-for="highlight in highlights"
          :key="highlight.label"
          :class="[ui.item, highlight.unavailable && ui.unavailableItem]"
        >
          <span :class="highlight.unavailable ? ui.unavailableLabel : ui.label">{{ highlight.label }}</span>
          <span v-if="highlight.missing" :class="ui.missingValue">{{ MISSING_KPI_VALUE }}</span>
          <span v-else-if="highlight.unavailable" :class="ui.unavailableMention">{{ UNAVAILABLE_KPI_LABEL }}</span>
          <AwIcon
            v-else
            :name="statusIconName(highlight.status)"
            :class="ui.statusIcon"
            :style="statusIconStyle(highlight.status)"
          />
        </div>
      </template>
    </div>
  </ConsultantKpiCard>
</template>
